<template>
  <div class="shuttle">
    <div class="shuttle-box">
      <div class="shuttle-title">Available</div>
      <div v-if="showAvailableFilter" class="shuttle-filter-wrap">
        <input
          v-model.trim="availableFilter"
          type="text"
          class="shuttle-filter"
          placeholder="Filter available..."
        />
      </div>
      <div class="shuttle-list">
        <button
          v-for="item in filteredAvailableItems"
          :key="`available-${item.value}`"
          type="button"
          class="shuttle-item"
          :class="{ selected: availableSelection.includes(item.value) }"
          @click="toggleAvailableSelection(item.value, $event)"
        >
          <span class="shuttle-item-content">
            <AppIcon
              v-if="item.iconKey"
              class="shuttle-icon"
              :name="item.iconKey"
              :color="item.iconColor"
              :size="16"
            />
            <FavIcon
              v-else-if="item.domain"
              class="shuttle-icon"
              :domain="item.domain"
              :size="16"
            />
            <HeroIcon
              v-else-if="item.iconName"
              class="shuttle-icon"
              :name="item.iconName"
              :iconStyle="item.iconStyle"
              :color="item.iconColor"
              :size="16"
              aria-hidden="true"
            />
            <img
              v-else-if="item.icon"
              :src="item.icon"
              class="shuttle-icon"
              alt=""
              aria-hidden="true"
              loading="lazy"
              draggable="false"
            />
            <span class="shuttle-label">{{ item.label }}</span>
          </span>
        </button>
        <div v-if="!filteredAvailableItems.length" class="muted shuttle-empty">{{ availableEmptyStateText }}</div>
      </div>
    </div>

    <div class="shuttle-actions">
      <button type="button" class="btn" :disabled="!availableSelection.length || isAtMaxLimit" @click="handleAdd">Add →</button>
      <button type="button" class="btn" :disabled="!selectedSelection.length" @click="handleRemove">← Remove</button>
      <template v-if="showReorder">
        <div class="shuttle-actions-spacer"></div>
        <button type="button" class="btn" :disabled="!canMoveUp" @click="handleMoveUp" title="Move up">↑</button>
        <button type="button" class="btn" :disabled="!canMoveDown" @click="handleMoveDown" title="Move down">↓</button>
      </template>
    </div>

    <div class="shuttle-box">
      <div class="shuttle-title">
        Selected
        <span v-if="maxSelected !== null" class="shuttle-count">({{ selectedItems.length }}/{{ maxSelected }})</span>
      </div>
      <div v-if="showSelectedFilter" class="shuttle-filter-wrap">
        <input
          v-model.trim="selectedFilter"
          type="text"
          class="shuttle-filter"
          placeholder="Filter selected..."
        />
      </div>
      <div class="shuttle-list">
        <button
          v-for="item in filteredSelectedItems"
          :key="`selected-${item.value}`"
          type="button"
          class="shuttle-item"
          :class="{ selected: selectedSelection.includes(item.value) }"
          @click="toggleSelectedSelection(item.value, $event)"
        >
          <span class="shuttle-item-content">
            <AppIcon
              v-if="item.iconKey"
              class="shuttle-icon"
              :name="item.iconKey"
              :color="item.iconColor"
              :size="16"
            />
            <FavIcon
              v-else-if="item.domain"
              class="shuttle-icon"
              :domain="item.domain"
              :size="16"
            />
            <HeroIcon
              v-else-if="item.iconName"
              class="shuttle-icon"
              :name="item.iconName"
              :iconStyle="item.iconStyle"
              :color="item.iconColor"
              :size="16"
              aria-hidden="true"
            />
            <img
              v-else-if="item.icon"
              :src="item.icon"
              class="shuttle-icon"
              alt=""
              aria-hidden="true"
              loading="lazy"
              draggable="false"
            />
            <span class="shuttle-label">{{ item.label }}</span>
          </span>
        </button>
        <div v-if="!filteredSelectedItems.length" class="muted shuttle-empty">{{ selectedEmptyStateText }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import AppIcon from './AppIcon.vue'
import FavIcon from './FavIcon.vue'
import HeroIcon from './HeroIcon.vue'

const props = defineProps({
  availableItems: {
    type: Array,
    default: () => []
  },
  selectedItems: {
    type: Array,
    default: () => []
  },
  emptyAvailableText: {
    type: String,
    default: 'No more available items.'
  },
  emptySelectedText: {
    type: String,
    default: 'No items selected yet.'
  },
  filterThreshold: {
    type: Number,
    default: 8
  },
  maxSelected: {
    type: Number,
    default: null
  },
  showReorder: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['add', 'remove', 'reorder'])

const availableSelection = ref([])
const selectedSelection = ref([])
const availableFilter = ref('')
const selectedFilter = ref('')

const lastAvailableClick = ref(null)
const lastSelectedClick = ref(null)

const isAtMaxLimit = computed(() => {
  if (props.maxSelected === null) return false
  return props.selectedItems.length >= props.maxSelected
})

const showAvailableFilter = computed(() => props.availableItems.length > props.filterThreshold)

const showSelectedFilter = computed(() => props.selectedItems.length > props.filterThreshold)

const filteredAvailableItems = computed(() => {
  const query = availableFilter.value.trim().toLowerCase()
  if (!query) return props.availableItems
  return props.availableItems.filter(item => matchesFilter(item, query))
})

const filteredSelectedItems = computed(() => {
  const query = selectedFilter.value.trim().toLowerCase()
  if (!query) return props.selectedItems
  return props.selectedItems.filter(item => matchesFilter(item, query))
})

const availableEmptyStateText = computed(() => {
  if (filteredAvailableItems.value.length) return ''
  return availableFilter.value.trim() ? 'No matching available items.' : props.emptyAvailableText
})

const selectedEmptyStateText = computed(() => {
  if (filteredSelectedItems.value.length) return ''
  return selectedFilter.value.trim() ? 'No matching selected items.' : props.emptySelectedText
})

function matchesFilter(item, query) {
  return String(item?.label || '').toLowerCase().includes(query)
}

function toggleAvailableSelection(value, event) {
  const idx = availableSelection.value.indexOf(value)
  
  if (event.shiftKey && lastAvailableClick.value !== null) {
    // Shift-click: range selection
    const items = filteredAvailableItems.value.map(item => item.value)
    const startIdx = items.indexOf(lastAvailableClick.value)
    const endIdx = items.indexOf(value)
    const [min, max] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx]
    
    const rangeValues = items.slice(min, max + 1)
    availableSelection.value = [...new Set([...availableSelection.value, ...rangeValues])]
  } else if (event.ctrlKey || event.metaKey) {
    // Ctrl/Cmd-click: toggle multi-select
    if (idx >= 0) {
      availableSelection.value.splice(idx, 1)
    } else {
      availableSelection.value.push(value)
    }
  } else {
    // Regular click: single select
    availableSelection.value = [value]
  }
  
  lastAvailableClick.value = value
}

function toggleSelectedSelection(value, event) {
  const idx = selectedSelection.value.indexOf(value)
  
  if (event.shiftKey && lastSelectedClick.value !== null) {
    // Shift-click: range selection
    const items = filteredSelectedItems.value.map(item => item.value)
    const startIdx = items.indexOf(lastSelectedClick.value)
    const endIdx = items.indexOf(value)
    const [min, max] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx]
    
    const rangeValues = items.slice(min, max + 1)
    selectedSelection.value = [...new Set([...selectedSelection.value, ...rangeValues])]
  } else if (event.ctrlKey || event.metaKey) {
    // Ctrl/Cmd-click: toggle multi-select
    if (idx >= 0) {
      selectedSelection.value.splice(idx, 1)
    } else {
      selectedSelection.value.push(value)
    }
  } else {
    // Regular click: single select
    selectedSelection.value = [value]
  }
  
  lastSelectedClick.value = value
}

const canMoveUp = computed(() => {
  if (selectedSelection.value.length !== 1) return false
  const idx = props.selectedItems.findIndex((i) => i.value === selectedSelection.value[0])
  return idx > 0
})

const canMoveDown = computed(() => {
  if (selectedSelection.value.length !== 1) return false
  const idx = props.selectedItems.findIndex((i) => i.value === selectedSelection.value[0])
  return idx >= 0 && idx < props.selectedItems.length - 1
})

function handleAdd() {
  emit('add', [...availableSelection.value])
  availableSelection.value = []
}

function handleRemove() {
  emit('remove', [...selectedSelection.value])
  selectedSelection.value = []
}

function handleMoveUp() {
  if (!canMoveUp.value) return
  const val = selectedSelection.value[0]
  const items = props.selectedItems.map((i) => i.value)
  const idx = items.indexOf(val)
  items.splice(idx - 1, 0, items.splice(idx, 1)[0])
  emit('reorder', items)
}

function handleMoveDown() {
  if (!canMoveDown.value) return
  const val = selectedSelection.value[0]
  const items = props.selectedItems.map((i) => i.value)
  const idx = items.indexOf(val)
  items.splice(idx + 1, 0, items.splice(idx, 1)[0])
  emit('reorder', items)
}
</script>

<style scoped>
.shuttle {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 10px;
  align-items: stretch;
  height: 400px;
  max-height: 60vh;
}

.shuttle-box {
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.shuttle-title {
  padding: 8px 10px;
  border-bottom: 1px solid var(--border);
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.shuttle-filter-wrap {
  padding: 8px 10px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.shuttle-filter {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg);
  color: var(--text);
  padding: 7px 10px;
  font: inherit;
}

.shuttle-filter:focus {
  outline: none;
  border-color: var(--selection-border);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--selection-border) 18%, transparent);
}

.shuttle-count {
  font-weight: 600;
  color: var(--text);
}

.shuttle-list {
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.shuttle-item {
  width: 100%;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text);
  border-radius: 8px;
  padding: 7px 8px;
  text-align: left;
  cursor: pointer;
}

.shuttle-item:hover {
  background: var(--row-hover-bg);
}

.shuttle-item.selected {
  background: var(--selection-bg);
  border-color: var(--selection-border);
}

.shuttle-item-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.shuttle-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.shuttle-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.shuttle-empty {
  font-size: 12px;
  padding: 8px;
  color: var(--muted);
}

.shuttle-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}

.shuttle-actions-spacer {
  height: 8px;
}

@media (max-width: 980px) {
  .shuttle {
    grid-template-columns: 1fr;
  }

  .shuttle-actions {
    flex-direction: row;
    justify-content: flex-start;
  }
}
</style>
